import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import type { AgencyPrintTheme } from "../utils/agencyPrint";
import {
  Copy,
  ExternalLink,
  FileStack,
  Film,
  Loader2,
  RefreshCcw,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import AgencyPrintOptionsModal from "../components/agency/AgencyPrintOptionsModal";
import CustomOrderOwnerAccessGate from "../components/custom/CustomOrderOwnerAccessGate";
import { renderEvidenceSections } from "./club/admin/ClubAdminCasePreviewPage";
import { normalizeEvidenceImageRecords } from "../utils/clubAssetRefs";
import {
  CASE_VIEW_CARD_TITLE_CLASS,
  CASE_VIEW_DISPLAY_TITLE_CLASS,
  CASE_VIEW_HERO_READING_CLASS,
  CASE_VIEW_METRIC_VALUE_CLASS,
  CASE_VIEW_PRIMARY_READING_CLASS,
  CASE_VIEW_SECTION_HEADING_CLASS,
  CASE_VIEW_SUPPORTING_READING_CLASS,
} from "../utils/caseViewTypography";
import { slugifyText } from "../utils/clubCasePath";
import { CustomOrderAPI } from "../lib/api";
import { getLocalizedCustomCaseApiMessage } from "../utils/customCaseApiErrors";
import { devError, devLog } from "../utils/devLog";
import {
  buildCustomCaseViewerSearch,
  formatCustomCaseViewerDateTime,
  getCustomCaseViewerCopy,
  getLocalizedCustomCaseEvidenceTypeLabel,
  resolveCustomCaseViewerLang,
} from "../utils/customCaseViewerLocalization";

type CustomCaseBriefingVideoRecord = {
  url: string;
  language: string;
  source: string;
  usedFallback: boolean;
  isLocaleSpecific: boolean;
  hasStoredValue: boolean;
  posterUrl?: string;
  captionsUrl?: string;
  transcriptUrl?: string;
};

type CustomCaseAccessRecord = {
  caseIndex?: number;
  available: boolean;
  status: string;
  customerFirstName?: string;
  title?: string;
  summary?: string;
  briefingTitle?: string;
  briefingBody?: string;
  generatedCaseId?: string;
  renderStatus?: string;
  language?: string;
  briefingVideoUrl?: string;
  briefingVideo?: CustomCaseBriefingVideoRecord;
  casePayload?: Record<string, unknown> | null;
  evidenceImageAccessMap?: Record<string, string>;
  suspectImageAccessMap?: Record<string, string>;
  heroImageUrl?: string;
  packetPdfUrl?: string;
  packetPdfExpiresAt?: number;
  caseAccessPath?: string;
  caseAccessUrl?: string;
  suspects?: Array<{
    name: string;
    relation?: string;
    occupation?: string;
    imageUrl?: string;
  }>;
  evidenceHighlights?: Array<{
    id: string;
    type: string;
    title: string;
    imageCount: number;
  }>;
  solveStatus?: {
    attemptCount?: number;
    hasSubmittedWarrant: boolean;
    selectedSuspectIndex: number | null;
    selectedSuspectName: string;
    isCorrect: boolean;
    solutionUnlocked: boolean;
    submittedAt?: string | null;
    solutionUnlockedAt?: string | null;
  };
  access?: {
    granted: boolean;
    mode: string;
  };
};

const toEmbedUrl = (raw = "") => {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (value.includes("/embed/")) return value;

  const watchMatch = value.match(/[?&]v=([^&]+)/);
  if (watchMatch?.[1]) return `https://www.youtube.com/embed/${watchMatch[1]}`;

  const shortMatch = value.match(/youtu\.be\/([^?]+)/);
  if (shortMatch?.[1]) return `https://www.youtube.com/embed/${shortMatch[1]}`;

  return value;
};

const resolveLocalizedCustomWelcomeVideoUrl = (lang: string) => {
  const normalizedLang = String(lang || "").trim().toLowerCase();
  const localeEnvKey = `VITE_CUSTOM_WELCOME_URL_${normalizedLang.toUpperCase()}`;
  const localeEnvValue = String((import.meta.env as Record<string, string | undefined>)[localeEnvKey] || "").trim();
  if (/^https?:\/\//i.test(localeEnvValue)) {
    return {
      url: localeEnvValue,
      source: `env_${normalizedLang}`,
    };
  }

  const defaultEnvValue = String(import.meta.env.VITE_CUSTOM_WELCOME_URL || "").trim();
  if (/^https?:\/\//i.test(defaultEnvValue)) {
    return {
      url: defaultEnvValue,
      source: "env_default",
    };
  }

  return {
    url: "",
    source: "none",
  };
};

const sharedCaseAccessResponseCache = new Map<string, any>();
const sharedCaseAccessRequestCache = new Map<string, Promise<any>>();

const loadSharedCaseAccessWithCache = async (shareToken: string) => {
  const cacheKey = String(shareToken || "").trim();
  if (!cacheKey) return null;

  if (sharedCaseAccessResponseCache.has(cacheKey)) {
    return sharedCaseAccessResponseCache.get(cacheKey);
  }

  const pendingRequest = sharedCaseAccessRequestCache.get(cacheKey);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = CustomOrderAPI.getSharedCaseAccess(cacheKey)
    .then((response) => {
      sharedCaseAccessResponseCache.set(cacheKey, response);
      return response;
    })
    .finally(() => {
      sharedCaseAccessRequestCache.delete(cacheKey);
    });

  sharedCaseAccessRequestCache.set(cacheKey, request);
  return request;
};

const invalidateSharedCaseAccessCache = (shareToken: string) => {
  const cacheKey = String(shareToken || "").trim();
  if (!cacheKey) return;
  sharedCaseAccessResponseCache.delete(cacheKey);
  sharedCaseAccessRequestCache.delete(cacheKey);
};

const getEvidenceFiles = (caseData: CustomCaseAccessRecord | null) => {
  const rawPayload = caseData?.casePayload as any;
  return Array.isArray(rawPayload?.evidence_files) ? rawPayload.evidence_files : [];
};

const isHttpUrl = (value: string) => /^https?:\/\//i.test(String(value || "").trim());

const resolveDirectUrlFromImageRecord = (image: any) => {
  if (typeof image === "string") {
    const direct = String(image || "").trim();
    return isHttpUrl(direct) ? direct : "";
  }
  if (!image || typeof image !== "object" || Array.isArray(image)) return "";

  const candidates = [image.url, image.image_url, image.imageUrl, image.src, image.access?.url, image.signedUrl];
  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (isHttpUrl(value)) return value;
  }
  return "";
};

const resolveEvidenceImageRecords = (images: any[], accessMap: Record<string, string> = {}) =>
  (Array.isArray(images) ? images : [])
    .map((rawImage) => {
      const [image] = normalizeEvidenceImageRecords([rawImage]);
      const storageKey = String(image?.storageKey || "").trim();
      const accessUrl = String(accessMap[storageKey] || "").trim();
      const payloadUrl = resolveDirectUrlFromImageRecord(rawImage);
      const resolvedUrl = isHttpUrl(storageKey) ? storageKey : accessUrl || payloadUrl;
      if (!storageKey && !resolvedUrl) return null;

      return {
        storageKey,
        description: String(image?.description || "").trim(),
        url: resolvedUrl,
      };
    })
    .filter(Boolean) as Array<{ storageKey: string; description: string; url: string }>;

const getEvidenceThumbnailUrl = (evidenceFile: any, imageAccessMap: Record<string, string>) => {
  const normalizedImages = normalizeEvidenceImageRecords(Array.isArray(evidenceFile?.images) ? evidenceFile.images : []);
  for (const image of normalizedImages) {
    const directUrl = String((image as any)?.url || "").trim();
    if (directUrl) return directUrl;
    const signedUrl = String(imageAccessMap[String(image?.storageKey || "").trim()] || "").trim();
    if (signedUrl) return signedUrl;
  }
  return "";
};

const buildEvidenceId = (file: any, index: number) => {
  const explicitId = String(file?.id || "").trim();
  if (explicitId) return explicitId;
  const baseLabel = String(file?.title || file?.type || `evidence-${index + 1}`).trim();
  const slug = slugifyText(baseLabel) || `evidence-${index + 1}`;
  return `${String(index + 1).padStart(2, "0")}-${slug}`;
};

const getCaseDynamicCounts = (evidenceFiles: any[], suspects: any[]) => {
  const files = Array.isArray(evidenceFiles) ? evidenceFiles : [];
  const suspectList = Array.isArray(suspects) ? suspects : [];

  return {
    suspectCount: suspectList.length,
    evidenceCount: files.length,
    imageCount: files.reduce(
      (count, file) => count + normalizeEvidenceImageRecords(Array.isArray(file?.images) ? file.images : []).length,
      0
    ),
    sectionCount: files.reduce((count, file) => count + (Array.isArray(file?.sections) ? file.sections.length : 0), 0),
  };
};

const renderEvidencePager = (options: {
  previousItem?: { title?: string; type?: string; href: string } | null;
  nextItem?: { title?: string; type?: string; href: string } | null;
  copy: {
    previousFile: string;
    nextFile: string;
    previousEvidence: string;
    nextEvidence: string;
  };
  lang: Parameters<typeof getLocalizedCustomCaseEvidenceTypeLabel>[1];
}) => {
  const pagerItems = [
    options.previousItem
      ? {
          key: `previous-${options.previousItem.href}`,
          href: options.previousItem.href,
          direction: options.copy.previousFile,
          typeLabel: getLocalizedCustomCaseEvidenceTypeLabel(String(options.previousItem.type || ""), options.lang),
          title: String(options.previousItem.title || options.copy.previousEvidence).trim() || options.copy.previousEvidence,
          align: "items-start text-left",
        }
      : null,
    options.nextItem
      ? {
          key: `next-${options.nextItem.href}`,
          href: options.nextItem.href,
          direction: options.copy.nextFile,
          typeLabel: getLocalizedCustomCaseEvidenceTypeLabel(String(options.nextItem.type || ""), options.lang),
          title: String(options.nextItem.title || options.copy.nextEvidence).trim() || options.copy.nextEvidence,
          align: "items-end text-right",
        }
      : null,
  ].filter(Boolean) as Array<{
    key: string;
    href: string;
    direction: string;
    typeLabel: string;
    title: string;
    align: string;
  }>;

  if (!pagerItems.length) return null;

  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-2">
      {pagerItems.map((item) => (
        <Link
          key={item.key}
          to={item.href}
          className={`flex min-h-[112px] flex-col justify-between rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] px-5 py-4 transition hover:border-[#7ed0e3]/35 hover:bg-[linear-gradient(180deg,rgba(42,81,109,0.22),rgba(255,255,255,0.03))] ${item.align}`}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#d0c296]">{item.direction}</p>
            <p className="mt-2 text-sm font-semibold uppercase tracking-[0.14em] text-[#7ed0e3]">{item.typeLabel}</p>
          </div>
          <p className="mt-3 text-lg font-semibold leading-tight text-white">{item.title}</p>
        </Link>
      ))}
    </div>
  );
};

const resolveBriefingVideoUrl = (
  caseAccess: CustomCaseAccessRecord | null,
  payload: any,
  lang: string,
) => {
  const metadataUrl = String(caseAccess?.briefingVideo?.url || "").trim();
  if (/^https?:\/\//i.test(metadataUrl)) {
    return {
      url: metadataUrl,
      source: String(caseAccess?.briefingVideo?.source || "case_access_briefing_video").trim() || "case_access_briefing_video",
      usedFallback: Boolean(caseAccess?.briefingVideo?.usedFallback),
      hasStoredValue: Boolean(caseAccess?.briefingVideo?.hasStoredValue),
    };
  }

  const directCandidates = [
    { value: caseAccess?.briefingVideoUrl, source: "case_access_briefing_video_url" },
    { value: payload?.briefingVideoUrl, source: "payload_briefingVideoUrl" },
    { value: payload?.briefing_video_url, source: "payload_briefing_video_url" },
  ];

  for (const candidate of directCandidates) {
    const value = String(candidate.value || "").trim();
    if (!value) continue;
    if (/^https?:\/\//i.test(value)) {
      return {
        url: value,
        source: candidate.source,
        usedFallback: false,
        hasStoredValue: true,
      };
    }
  }

  return {
    ...resolveLocalizedCustomWelcomeVideoUrl(lang),
    usedFallback: lang !== "en",
    hasStoredValue: false,
  };
};

const isBuildCompleteStatus = (status: string) => ["delivery_ready", "delivered"].includes(status);
const shouldRedirectOwnerAccessToIntake = (status: string) => !isBuildCompleteStatus(status);

const statusCopy = (status: string, copy: Record<string, string>) => {
  if (status === "delivery_ready" || status === "delivered") {
    return copy.delivered || copy.delivery_ready || copy.default;
  }
  if (status === "review_required") {
    return copy.review_required || copy.default;
  }
  if (status === "generation_running" || status === "generation_queued") {
    return copy[status] || copy.default;
  }
  if (status === "generation_failed") {
    return copy.generation_failed || copy.default;
  }
  return copy.default;
};

export default function CustomCaseAccessPage() {
  const { accessToken = "", shareToken = "", evidenceId = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [caseAccess, setCaseAccess] = useState<CustomCaseAccessRecord | null>(null);
  const [copied, setCopied] = useState(false);
  const [warrantBusy, setWarrantBusy] = useState(false);
  const [warrantFeedback, setWarrantFeedback] = useState("");
  const [warrantError, setWarrantError] = useState("");
  const [printTheme, setPrintTheme] = useState<AgencyPrintTheme>("light");
  const [selectedPrintEvidenceIds, setSelectedPrintEvidenceIds] = useState<string[]>([]);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [pdfDownloadBusy, setPdfDownloadBusy] = useState(false);
  const [ownerVerified, setOwnerVerified] = useState(false);
  const [shareLinks, setShareLinks] = useState<
    Array<{
      id: string;
      label: string;
      mode: string;
      expiresAt?: string | null;
      createdAt?: string | null;
      accessCount: number;
      sharePath: string;
      shareUrl: string;
    }>
  >([]);
  const [shareDurationHours] = useState(24);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareFeedback, setShareFeedback] = useState("");
  const [shareError, setShareError] = useState("");
  const [lastCreatedShareUrl, setLastCreatedShareUrl] = useState("");

  const isSharedAccess = Boolean(shareToken) && !accessToken;

  useEffect(() => {
    setWarrantBusy(false);
    setWarrantFeedback("");
    setWarrantError("");
    devLog("route", "custom_case_access_warrant_state_reset", {
      accessTokenPresent: Boolean(accessToken),
      shareTokenPresent: Boolean(shareToken),
      accessMode: isSharedAccess ? "share" : "owner",
      generatedCaseId: String(caseAccess?.generatedCaseId || ""),
    });
  }, [accessToken, caseAccess?.generatedCaseId, isSharedAccess, shareToken]);

  const loadCaseAccess = async ({ silent = false } = {}) => {
    if (!accessToken && !shareToken) return;
    if (!silent) setLoading(true);
    if (silent) setRefreshing(true);
    setError("");

    devLog("route", "custom_case_access_load_started", {
      accessTokenPresent: Boolean(accessToken),
      shareTokenPresent: Boolean(shareToken),
      accessMode: isSharedAccess ? "share" : "owner",
      silent,
      evidenceRoute: Boolean(evidenceId),
    });

    try {
      const response = isSharedAccess
        ? await loadSharedCaseAccessWithCache(shareToken)
        : await CustomOrderAPI.getCaseAccess(accessToken);
      const nextCaseAccess = response?.caseAccess || null;
      setCaseAccess(nextCaseAccess);
      setWarrantError("");
      devLog("route", "custom_case_access_load_succeeded", {
        accessTokenPresent: Boolean(accessToken),
        shareTokenPresent: Boolean(shareToken),
        accessMode: isSharedAccess ? "share" : "owner",
        status: String(nextCaseAccess?.status || ""),
        available: Boolean(nextCaseAccess?.available),
        generatedCaseId: String(nextCaseAccess?.generatedCaseId || ""),
        playableEvidenceCount: getEvidenceFiles(nextCaseAccess).length,
      });
    } catch (err: any) {
      const message = resolveApiErrorMessage(err, viewerCopy.errors.loadCase);
      setError(message);
      devError("route", "custom_case_access_load_failed", {
        accessTokenPresent: Boolean(accessToken),
        shareTokenPresent: Boolean(shareToken),
        accessMode: isSharedAccess ? "share" : "owner",
        message,
        status: err?.response?.status,
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    devLog("route", "custom_case_access_route_entered", {
      accessTokenPresent: Boolean(accessToken),
      shareTokenPresent: Boolean(shareToken),
      accessMode: isSharedAccess ? "share" : "owner",
      evidenceRoute: Boolean(evidenceId),
    });
    if (isSharedAccess) {
      void loadCaseAccess();
    }
  }, [accessToken, evidenceId, isSharedAccess, shareToken]);

  useEffect(() => {
    if (!accessToken || !ownerVerified || isSharedAccess) return;
    void loadCaseAccess();
  }, [accessToken, isSharedAccess, ownerVerified]);

  useEffect(() => {
    const nextStatus = String(caseAccess?.status || "").trim();
    if (!accessToken || isSharedAccess || !ownerVerified || !nextStatus) return;
    if (!shouldRedirectOwnerAccessToIntake(nextStatus)) return;
    const targetSearch = location.search || "";

    devLog("route", "custom_case_access_redirected_to_intake", {
      accessTokenPresent: true,
      accessMode: "owner",
      status: nextStatus,
      generatedCaseId: String(caseAccess?.generatedCaseId || ""),
      targetSearch,
    });
    navigate(`/custom/intake/${accessToken}${targetSearch}`, { replace: true });
  }, [accessToken, caseAccess?.generatedCaseId, caseAccess?.status, isSharedAccess, location.search, navigate, ownerVerified]);

  useEffect(() => {
    const nextStatus = String(caseAccess?.status || "").trim();
    if ((!accessToken && !shareToken) || !nextStatus || isBuildCompleteStatus(nextStatus)) return;

    devLog("route", "custom_case_access_poll_started", {
      accessTokenPresent: Boolean(accessToken),
      shareTokenPresent: Boolean(shareToken),
      accessMode: isSharedAccess ? "share" : "owner",
      status: nextStatus,
    });

    const timer = window.setInterval(() => {
      void loadCaseAccess({ silent: true });
    }, 15000);

    return () => {
      window.clearInterval(timer);
      devLog("route", "custom_case_access_poll_stopped", {
        accessTokenPresent: Boolean(accessToken),
        shareTokenPresent: Boolean(shareToken),
        accessMode: isSharedAccess ? "share" : "owner",
        status: nextStatus,
      });
    };
  }, [accessToken, caseAccess?.status, isSharedAccess, shareToken]);

  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      // ignore non-browser runtimes
    }
  }, [location.pathname, evidenceId]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
      devLog("route", "custom_case_access_link_copied", {
        accessTokenPresent: Boolean(accessToken),
      });
    } catch (err: any) {
      devError("route", "custom_case_access_link_copy_failed", {
        message: err?.message || "clipboard_failed",
      });
    }
  };

  const handleCreateShareLink = async () => {
    if (!accessToken || isSharedAccess) return;
    try {
      setShareBusy(true);
      setShareError("");
      setShareFeedback("");
      devLog("route", "custom_case_share_link_create_started", {
        route: "/custom/case/:accessToken",
        expiresInHours: shareDurationHours,
      });
      const response = await CustomOrderAPI.createShareLink(accessToken, {
        expiresInHours: shareDurationHours,
        mode: "viewer",
      });
      setShareLinks(Array.isArray(response?.shareLinks) ? response.shareLinks : []);
      setLastCreatedShareUrl(String(response?.shareLink?.shareUrl || "").trim());
      setShareFeedback(viewerCopy.share.created);
      devLog("route", "custom_case_share_link_create_succeeded", {
        route: "/custom/case/:accessToken",
        shareLinkId: String(response?.shareLink?.id || ""),
        expiresInHours: shareDurationHours,
      });
    } catch (err: any) {
      const nextMessage = resolveApiErrorMessage(
        err,
        viewerCopy.errors.createGuestLink,
      );
      setShareError(nextMessage);
      devError("route", "custom_case_share_link_create_failed", {
        route: "/custom/case/:accessToken",
        message: nextMessage,
        status: err?.response?.status,
      });
    } finally {
      setShareBusy(false);
    }
  };

  const handleCopyCreatedShareLink = async () => {
    if (!lastCreatedShareUrl) return;
    try {
      await navigator.clipboard.writeText(lastCreatedShareUrl);
      setShareFeedback(viewerCopy.share.copied);
    } catch (err: any) {
      setShareError(String(err?.message || "").trim() || viewerCopy.errors.copyGuestLink);
    }
  };

  const handleRevokeShareLink = async (shareLinkId: string) => {
    if (!accessToken || !shareLinkId || isSharedAccess) return;
    try {
      setShareError("");
      setShareFeedback("");
      const response = await CustomOrderAPI.revokeShareLink(accessToken, shareLinkId);
      setShareLinks(Array.isArray(response?.shareLinks) ? response.shareLinks : []);
      setShareFeedback(viewerCopy.share.revoked);
      devLog("route", "custom_case_share_link_revoke_succeeded", {
        route: "/custom/case/:accessToken",
        shareLinkId,
      });
    } catch (err: any) {
      const nextMessage = resolveApiErrorMessage(
        err,
        viewerCopy.errors.revokeGuestLink,
      );
      setShareError(nextMessage);
      devError("route", "custom_case_share_link_revoke_failed", {
        route: "/custom/case/:accessToken",
        shareLinkId,
        message: nextMessage,
        status: err?.response?.status,
      });
    }
  };

  const payload = (caseAccess?.casePayload || {}) as any;
  const localeResolution = resolveCustomCaseViewerLang({
    search: location.search,
    caseAccessLanguage: caseAccess?.language,
    casePayloadLanguage: payload?.language || payload?.contentLanguage || payload?.content_language,
  });
  const viewerLang = localeResolution.lang;
  const viewerCopy = getCustomCaseViewerCopy(viewerLang);
  const currentCaseSearch = buildCustomCaseViewerSearch({
    lang: viewerLang,
    force: localeResolution.hasExplicitQuery || viewerLang !== "en",
  });
  const resolveApiErrorMessage = (error: any, fallback: string) =>
    getLocalizedCustomCaseApiMessage({
      error,
      locale: viewerLang,
      fallback,
    });
  const title = String(caseAccess?.title || viewerCopy.defaultTitle).trim() || viewerCopy.defaultTitle;
  const summary = String(caseAccess?.summary || "").trim() || viewerCopy.defaultSummary;
  const status = String(caseAccess?.status || "").trim();
  const evidenceImageAccessMap = (caseAccess?.evidenceImageAccessMap || {}) as Record<string, string>;
  const suspectImageAccessMap = (caseAccess?.suspectImageAccessMap || {}) as Record<string, string>;
  const resolvedBriefingVideo = resolveBriefingVideoUrl(caseAccess, payload, viewerLang);
  const rawBriefingVideoUrl = resolvedBriefingVideo.url;
  const briefingVideoUrl = toEmbedUrl(rawBriefingVideoUrl);
  const rawBriefingBody = String(
    payload?.briefing_body || payload?.welcome_email || caseAccess?.briefingBody || ""
  ).trim();
  const ownerFirstName = String(caseAccess?.customerFirstName || "").trim();
  const briefingRecipient = isSharedAccess ? viewerCopy.warrant.detectiveLabel : ownerFirstName || viewerCopy.warrant.detectiveLabel;
  const briefingBody = rawBriefingBody.replace(/\{first\}/gi, briefingRecipient);
  const evidenceFiles = useMemo(() => getEvidenceFiles(caseAccess), [caseAccess]);
  const printableEvidenceIds = useMemo(
    () =>
      evidenceFiles
        .map((file: any) => String(file?.id || "").trim())
        .filter(Boolean),
    [evidenceFiles]
  );
  const canCreateOwnerPdf = !isSharedAccess && Boolean(accessToken) && printableEvidenceIds.length > 0;
  const canOpenOwnerPacketPdf = !isSharedAccess && Boolean(caseAccess?.packetPdfUrl);
  const suspects = Array.isArray(payload?.suspects) ? payload.suspects : [];
  const evidenceWithIds = useMemo(
    () =>
      evidenceFiles.map((file: any, index: number) => ({
        file,
        index,
        evidenceId: buildEvidenceId(file, index),
      })),
    [evidenceFiles]
  );
  const activeEvidenceIndex = evidenceWithIds.findIndex((item) => item.evidenceId === evidenceId);
  const activeEvidence = activeEvidenceIndex >= 0 ? evidenceWithIds[activeEvidenceIndex]?.file || null : null;
  const hasPlayableCase = isBuildCompleteStatus(status) && evidenceFiles.length > 0;
  const baseCasePath = isSharedAccess
    ? `/custom/share/${encodeURIComponent(shareToken)}`
    : `/custom/case/${encodeURIComponent(accessToken)}`;
  const buildCaseHref = (suffix = "") => `${baseCasePath}${suffix}${currentCaseSearch}`;
  const currentCaseBasePath = buildCaseHref();
  const caseEntrySessionKey = isSharedAccess
    ? `custom_case_entry_seen_share_${shareToken}`
    : `custom_case_entry_seen_owner_${accessToken}`;

  useEffect(() => {
    if (!printableEvidenceIds.length) {
      setSelectedPrintEvidenceIds([]);
      return;
    }
    setSelectedPrintEvidenceIds((current) => {
      const filtered = current.filter((entry) => printableEvidenceIds.includes(entry));
      return filtered.length ? filtered : printableEvidenceIds;
    });
  }, [printableEvidenceIds]);

  useEffect(() => {
    devLog("route", "custom_case_access_locale_resolved", {
      accessTokenPresent: Boolean(accessToken),
      shareTokenPresent: Boolean(shareToken),
      accessMode: isSharedAccess ? "share" : "owner",
      language: viewerLang,
      source: localeResolution.source,
      rawValue: localeResolution.rawValue,
      hasExplicitQuery: localeResolution.hasExplicitQuery,
      usedFallback: localeResolution.usedFallback,
    });
  }, [
    accessToken,
    isSharedAccess,
    localeResolution.hasExplicitQuery,
    localeResolution.rawValue,
    localeResolution.source,
    localeResolution.usedFallback,
    shareToken,
    viewerLang,
  ]);

  useEffect(() => {
    if (!rawBriefingVideoUrl && !briefingVideoUrl) return;
    devLog("route", "custom_case_access_video_locale_resolved", {
      accessTokenPresent: Boolean(accessToken),
      shareTokenPresent: Boolean(shareToken),
      accessMode: isSharedAccess ? "share" : "owner",
      language: viewerLang,
      languageSource: localeResolution.source,
      rawValue: localeResolution.rawValue,
      videoSource: resolvedBriefingVideo.source,
      embedded: Boolean(briefingVideoUrl),
    });
  }, [
    accessToken,
    briefingVideoUrl,
    isSharedAccess,
    localeResolution.rawValue,
    localeResolution.source,
    rawBriefingVideoUrl,
    resolvedBriefingVideo.source,
    shareToken,
    viewerLang,
  ]);

  useEffect(() => {
    if (!currentCaseBasePath) return;

    try {
      const hasSeenBaseCasePage = window.sessionStorage.getItem(caseEntrySessionKey) === "1";
      if (!evidenceId) {
        window.sessionStorage.setItem(caseEntrySessionKey, "1");
        devLog("route", "custom_case_access_entry_marked_base", {
          accessTokenPresent: Boolean(accessToken),
          shareTokenPresent: Boolean(shareToken),
          accessMode: isSharedAccess ? "share" : "owner",
          path: location.pathname,
        });
        return;
      }

      if (!hasSeenBaseCasePage) {
        devLog("route", "custom_case_access_first_entry_redirected_to_base", {
          accessTokenPresent: Boolean(accessToken),
          shareTokenPresent: Boolean(shareToken),
          accessMode: isSharedAccess ? "share" : "owner",
          path: location.pathname,
          evidenceId,
          redirectTarget: currentCaseBasePath,
        });
        navigate(currentCaseBasePath, { replace: true });
      }
    } catch {
      if (evidenceId) {
        navigate(currentCaseBasePath, { replace: true });
      }
    }
  }, [
    accessToken,
    caseEntrySessionKey,
    currentCaseBasePath,
    evidenceId,
    isSharedAccess,
    location.pathname,
    navigate,
    shareToken,
  ]);

  const handleSubmitCustomWarrant = async (suspectIndex: number) => {
    if (!accessToken && !shareToken) return;
    try {
      setWarrantBusy(true);
      setWarrantError("");
      setWarrantFeedback("");
      devLog("route", "custom_case_access_warrant_submit_started", {
        accessTokenPresent: Boolean(accessToken),
        shareTokenPresent: Boolean(shareToken),
        accessMode: isSharedAccess ? "share" : "owner",
        suspectIndex,
        generatedCaseId: String(caseAccess?.generatedCaseId || ""),
      });
      const response = isSharedAccess
        ? await CustomOrderAPI.submitSharedWarrant(shareToken, { suspectIndex })
        : await CustomOrderAPI.submitWarrant(accessToken, { suspectIndex });
      if (isSharedAccess) {
        invalidateSharedCaseAccessCache(shareToken);
      }
      setCaseAccess((current) =>
        current
          ? {
              ...current,
              solveStatus: response?.solveStatus || current.solveStatus,
            }
          : current
      );
      setWarrantFeedback(
        response?.warrant?.isCorrect
          ? viewerCopy.warrant.approved
          : viewerCopy.warrant.rejected
      );
      devLog("route", "custom_case_access_warrant_submit_succeeded", {
        accessTokenPresent: Boolean(accessToken),
        shareTokenPresent: Boolean(shareToken),
        accessMode: isSharedAccess ? "share" : "owner",
        attemptCount: Number(response?.solveStatus?.attemptCount || 0),
        suspectIndex: Number(response?.warrant?.selectedSuspectIndex),
        isCorrect: Boolean(response?.warrant?.isCorrect),
        solutionUnlocked: Boolean(response?.solveStatus?.solutionUnlocked),
      });
    } catch (err: any) {
      const message = resolveApiErrorMessage(
        err,
        viewerCopy.errors.submitWarrant,
      );
      setWarrantError(message);
      devError("route", "custom_case_access_warrant_submit_failed", {
        accessTokenPresent: Boolean(accessToken),
        shareTokenPresent: Boolean(shareToken),
        accessMode: isSharedAccess ? "share" : "owner",
        message,
        status: err?.response?.status,
      });
    } finally {
      setWarrantBusy(false);
    }
  };

  const togglePrintEvidenceId = (targetEvidenceId: string) => {
    setSelectedPrintEvidenceIds((current) =>
      current.includes(targetEvidenceId)
        ? current.filter((entry) => entry !== targetEvidenceId)
        : [...current, targetEvidenceId]
    );
  };

  const handleToggleAllPrintEvidence = () => {
    setSelectedPrintEvidenceIds((current) =>
      current.length >= printableEvidenceIds.length ? [] : printableEvidenceIds
    );
  };

  const handleDownloadPdf = async () => {
    if (!accessToken || isSharedAccess) return;
    try {
      setPdfDownloadBusy(true);
      devLog("route", "custom_case_access_pdf_locale_resolved", {
        accessTokenPresent: Boolean(accessToken),
        accessMode: "owner",
        language: viewerLang,
        source: localeResolution.source,
        rawValue: localeResolution.rawValue,
      });
      devLog("route", "custom_case_access_pdf_download_started", {
        accessTokenPresent: Boolean(accessToken),
        accessMode: "owner",
        generatedCaseId: String(caseAccess?.generatedCaseId || ""),
        theme: printTheme,
        evidenceCount: selectedPrintEvidenceIds.length || printableEvidenceIds.length,
      });
      const response = await CustomOrderAPI.downloadCasePdf(accessToken, {
        theme: printTheme,
        evidenceIds: selectedPrintEvidenceIds,
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = String(response.headers?.["content-disposition"] || "");
      const filenameMatch = disposition.match(/filename="([^"]+)"/i);
      link.href = url;
      link.download =
        filenameMatch?.[1] ||
        `${String(title || "custom-case")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "") || "custom-case"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowPrintModal(false);
      devLog("route", "custom_case_access_pdf_download_succeeded", {
        accessTokenPresent: Boolean(accessToken),
        accessMode: "owner",
        generatedCaseId: String(caseAccess?.generatedCaseId || ""),
        theme: printTheme,
        filename: link.download,
      });
    } catch (err: any) {
      const message = resolveApiErrorMessage(
        err,
        viewerCopy.errors.downloadPdf,
      );
      setError(message);
      devError("route", "custom_case_access_pdf_download_failed", {
        accessTokenPresent: Boolean(accessToken),
        accessMode: "owner",
        generatedCaseId: String(caseAccess?.generatedCaseId || ""),
        message,
        status: err?.response?.status,
      });
    } finally {
      setPdfDownloadBusy(false);
    }
  };

  const handleDevResetCustomWarrant = async () => {
    if ((!accessToken && !shareToken) || !import.meta.env.DEV) return;
    try {
      setWarrantBusy(true);
      setWarrantError("");
      setWarrantFeedback("");
      devLog("route", "custom_case_access_warrant_dev_reset_started", {
        accessTokenPresent: Boolean(accessToken),
        shareTokenPresent: Boolean(shareToken),
        accessMode: isSharedAccess ? "share" : "owner",
        generatedCaseId: String(caseAccess?.generatedCaseId || ""),
      });
      const response = isSharedAccess
        ? await CustomOrderAPI.devResetSharedWarrant(shareToken)
        : await CustomOrderAPI.devResetWarrant(accessToken);
      setCaseAccess((current) =>
        current
          ? {
              ...current,
              solveStatus: response?.solveStatus || current.solveStatus,
            }
          : current
      );
      setWarrantFeedback(viewerCopy.warrant.reset);
      devLog("route", "custom_case_access_warrant_dev_reset_succeeded", {
        accessTokenPresent: Boolean(accessToken),
        shareTokenPresent: Boolean(shareToken),
        accessMode: isSharedAccess ? "share" : "owner",
        attemptCount: Number(response?.solveStatus?.attemptCount || 0),
        solutionUnlocked: Boolean(response?.solveStatus?.solutionUnlocked),
      });
    } catch (err: any) {
      const message = resolveApiErrorMessage(
        err,
        viewerCopy.errors.resetWarrant,
      );
      setWarrantError(message);
      devError("route", "custom_case_access_warrant_dev_reset_failed", {
        accessTokenPresent: Boolean(accessToken),
        shareTokenPresent: Boolean(shareToken),
        accessMode: isSharedAccess ? "share" : "owner",
        message,
        status: err?.response?.status,
      });
    } finally {
      setWarrantBusy(false);
    }
  };

  useEffect(() => {
    devLog("route", "custom_case_access_mode_resolved", {
      accessTokenPresent: Boolean(accessToken),
      shareTokenPresent: Boolean(shareToken),
      accessMode: isSharedAccess ? "share" : "owner",
      status,
      evidenceRoute: Boolean(evidenceId),
      hasPlayableCase,
      renderStatus: String(caseAccess?.renderStatus || ""),
    });
  }, [accessToken, caseAccess?.renderStatus, evidenceId, hasPlayableCase, isSharedAccess, shareToken, status]);

  if (!isSharedAccess && !ownerVerified) {
    return (
      <>
        <Helmet>
          <title>{viewerCopy.privateCaseTitle}</title>
        </Helmet>
        <section className="mx-auto max-w-5xl px-4 py-8 text-white">
          <CustomOrderOwnerAccessGate
            accessToken={accessToken}
            routeLabel="/custom/case/:accessToken"
            locale={viewerLang}
            title={viewerCopy.ownerGate.title}
            description={viewerCopy.ownerGate.description}
            copy={viewerCopy.ownerGate.copy}
            onVerified={() => setOwnerVerified(true)}
            onAccessStateResolved={(accessState) => {
              setShareLinks(Array.isArray(accessState.shareLinks) ? accessState.shareLinks : []);
            }}
          >
            <div />
          </CustomOrderOwnerAccessGate>
        </section>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{`${title} | ${viewerCopy.pageTitleSuffix}`}</title>
        </Helmet>
        <section className="min-h-screen bg-[#05070d] text-white">
          <div className="mx-auto flex min-h-[60vh] max-w-7xl items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm text-white/75">
              <Loader2 className="h-4 w-4 animate-spin" />
              {viewerCopy.loadingMessage}
            </div>
          </div>
        </section>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>{`${title} | ${viewerCopy.pageTitleSuffix}`}</title>
        </Helmet>
        <section className="min-h-screen bg-[#05070d] text-white">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="rounded-[28px] border border-rose-300/20 bg-rose-950/20 p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-rose-200/80">{viewerCopy.accessProblem}</p>
              <p className="mt-3 text-sm text-rose-50/90">{error}</p>
            </div>
          </div>
        </section>
      </>
    );
  }

  if (!hasPlayableCase) {
    const embeddedVideoUrl = briefingVideoUrl || toEmbedUrl(String(caseAccess?.briefingVideoUrl || ""));

    return (
      <>
        <Helmet>
          <title>{`${title} | ${viewerCopy.pageTitleSuffix}`}</title>
        </Helmet>

        <section className="min-h-screen bg-[#05070d] text-white">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="mb-6 flex items-center justify-between gap-4">
              <Link
                to={accessToken ? `/custom/intake/${accessToken}` : "/custom"}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/78 transition hover:bg-white/10 hover:text-white"
              >
                <RefreshCcw className="h-4 w-4" />
                {viewerCopy.buildStatus}
              </Link>

              <button
                type="button"
                onClick={() => void loadCaseAccess({ silent: true })}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/78 transition hover:bg-white/10 hover:text-white"
              >
                {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
                {viewerCopy.refresh}
              </button>
            </div>

            <div className="space-y-6">
              <div className="overflow-hidden rounded-[36px] border border-white/10 bg-[linear-gradient(135deg,rgba(9,12,20,0.98),rgba(7,18,24,0.96))] shadow-[0_40px_120px_rgba(0,0,0,0.45)]">
                <div className="grid gap-0 lg:grid-cols-[1.06fr_0.94fr]">
                  <div className="p-6 sm:p-8 lg:p-10">
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#d6b26b]/25 bg-[#d6b26b]/10 px-3 py-1 text-[11px] uppercase tracking-[0.24em] text-[#eed7a3]">
                      <ShieldCheck className="h-3.5 w-3.5" />
                      {viewerCopy.privateCaseBadge}
                    </div>
                    <h1 className="mt-5 max-w-3xl font-heading text-4xl font-bold leading-tight text-white sm:text-5xl">
                      {title}
                    </h1>
                    <p className="mt-4 max-w-2xl text-base leading-7 text-white/74">{summary}</p>

                    <div className="mt-5 flex flex-wrap gap-2">
                      <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-white/72">
                        {status.replaceAll("_", " ") || viewerCopy.metadata.internal}
                      </span>
                      {caseAccess?.generatedCaseId ? (
                        <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-white/72">
                          {caseAccess.generatedCaseId}
                        </span>
                      ) : null}
                    </div>

                    <p className={`mt-6 max-w-2xl text-white/68 ${CASE_VIEW_SUPPORTING_READING_CLASS}`}>
                      {statusCopy(status, viewerCopy.statusSummary)}
                    </p>

                    <div className="mt-8 flex flex-wrap gap-3">
                      {canCreateOwnerPdf ? (
                        <button
                          type="button"
                          onClick={() => {
                            devLog("route", "custom_case_access_pdf_modal_opened", {
                              accessTokenPresent: Boolean(accessToken),
                              accessMode: "owner",
                              generatedCaseId: String(caseAccess?.generatedCaseId || ""),
                              evidenceCount: selectedPrintEvidenceIds.length || printableEvidenceIds.length,
                            });
                            setShowPrintModal(true);
                          }}
                          className="inline-flex items-center gap-2 rounded-full border border-[#d0c296]/30 bg-[#d0c296]/10 px-5 py-3 text-sm font-semibold text-[#f6f0e0] transition hover:bg-[#d0c296]/18"
                        >
                          <FileStack className="h-4 w-4" />
                          {viewerCopy.createPdf}
                        </button>
                      ) : null}
                      {canOpenOwnerPacketPdf ? (
                        <a
                          href={caseAccess.packetPdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 rounded-full bg-[#d7263d] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#f23c56]"
                          onClick={() =>
                            devLog("route", "custom_case_access_packet_opened", {
                              accessTokenPresent: Boolean(accessToken),
                              generatedCaseId: String(caseAccess?.generatedCaseId || ""),
                            })
                          }
                        >
                          <ExternalLink className="h-4 w-4" />
                          {viewerCopy.openCasePacket}
                        </a>
                      ) : null}

                      <button
                        type="button"
                        onClick={handleCopyLink}
                        className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold text-white/86 transition hover:bg-white/10"
                      >
                        <Copy className="h-4 w-4" />
                        {copied ? viewerCopy.linkCopied : viewerCopy.copyPrivateLink}
                      </button>
                    </div>

                    <div className="mt-8 grid gap-3 sm:grid-cols-3">
                      <div className="rounded-[24px] border border-white/10 bg-white/[0.04] p-4">
                        <p className="text-[11px] uppercase tracking-[0.2em] text-white/45">{viewerCopy.caseState}</p>
                        <p className="mt-3 text-xl font-semibold capitalize text-white">
                          {status.replaceAll("_", " ") || viewerCopy.metadata.internal}
                        </p>
                      </div>
                      <div className="rounded-[24px] border border-white/10 bg-white/[0.04] p-4">
                        <p className="text-[11px] uppercase tracking-[0.2em] text-white/45">{viewerCopy.suspects}</p>
                        <p className="mt-3 text-xl font-semibold text-white">
                          {Array.isArray(caseAccess?.suspects) ? caseAccess.suspects.length : 0}
                        </p>
                      </div>
                      <div className="rounded-[24px] border border-white/10 bg-white/[0.04] p-4">
                        <p className="text-[11px] uppercase tracking-[0.2em] text-white/45">{viewerCopy.evidenceFiles}</p>
                        <p className="mt-3 text-xl font-semibold text-white">
                          {Array.isArray(caseAccess?.evidenceHighlights) ? caseAccess.evidenceHighlights.length : 0}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-white/8 lg:border-l lg:border-t-0">
                    {caseAccess?.heroImageUrl ? (
                      <div className="h-full min-h-[320px]">
                        <img
                          src={caseAccess.heroImageUrl}
                          alt={`${title} case background`}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="flex h-full min-h-[320px] items-center justify-center bg-[radial-gradient(circle_at_center,rgba(214,178,107,0.15),transparent_55%),linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.01))] p-8">
                        <div className="max-w-sm rounded-[28px] border border-white/10 bg-black/20 p-5">
                          <div className="flex items-center gap-3 text-[#d6b26b]">
                            <Film className="h-5 w-5" />
                            <span className="text-xs uppercase tracking-[0.24em] text-[#eed7a3]">{viewerCopy.caseHub}</span>
                          </div>
                          <p className="mt-4 text-sm leading-6 text-white/70">
                            {viewerCopy.caseHubBody}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {embeddedVideoUrl ? (
                <div className="grid gap-6 xl:grid-cols-[1.08fr_0.92fr]">
                  <div className="rounded-[32px] border border-white/10 bg-white/[0.03] p-5 sm:p-6">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-[#eed7a3]">{viewerCopy.detectiveBriefing}</p>
                    <div className="mt-4 overflow-hidden rounded-[24px] border border-white/10 bg-black/30">
                      <div className="aspect-video">
                        <iframe
                          src={embeddedVideoUrl}
                          title={viewerCopy.video.iframeTitle}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          className="h-full w-full"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-[32px] border border-white/10 bg-white/[0.03] p-5 sm:p-6">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-[#eed7a3]">{viewerCopy.whatOpensHere}</p>
                    <div className="mt-4 space-y-3">
                      <div className="rounded-[22px] border border-white/10 bg-black/20 p-4">
                        <p className="text-sm font-semibold text-white">{viewerCopy.fullCaseExperience}</p>
                        <p className={`mt-2 text-white/68 ${CASE_VIEW_SUPPORTING_READING_CLASS}`}>
                          {viewerCopy.fullCaseExperienceBody}
                        </p>
                      </div>
                      <div className="rounded-[22px] border border-white/10 bg-black/20 p-4">
                        <p className="text-sm font-semibold text-white">{viewerCopy.privateLinkCard}</p>
                        <p className={`mt-2 text-white/68 ${CASE_VIEW_SUPPORTING_READING_CLASS}`}>
                          {viewerCopy.privateLinkCardBody}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          <AgencyPrintOptionsModal
            open={showPrintModal}
            onClose={() => setShowPrintModal(false)}
            printTheme={printTheme}
            onThemeChange={setPrintTheme}
            evidenceFiles={evidenceFiles}
            selectedEvidenceIds={selectedPrintEvidenceIds}
            onEvidenceToggle={togglePrintEvidenceId}
            onToggleAllEvidence={handleToggleAllPrintEvidence}
            onDownloadPdf={handleDownloadPdf}
            pdfBusy={pdfDownloadBusy}
            copy={viewerCopy.printModal}
          />
        </section>
      </>
    );
  }

  if (evidenceId && !activeEvidence) {
    return (
      <>
        <Helmet>
          <title>{`${title} | ${viewerCopy.pageTitleSuffix}`}</title>
        </Helmet>
        <section className="mx-auto max-w-4xl px-4 py-6 text-white">
          <div className="rounded-2xl border border-white/15 bg-zinc-950/80 p-5">
            <p className="text-rose-300">{viewerCopy.evidenceFileNotFound}</p>
            <div className="mt-4">
              <Link
                to={currentCaseBasePath}
                className="rounded-md border border-white/20 px-3 py-2 text-xs font-semibold text-zinc-200 hover:bg-white/10"
              >
                {viewerCopy.backToCaseFile}
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  if (evidenceId && activeEvidence) {
    const selectedEvidenceImageAssets = resolveEvidenceImageRecords(activeEvidence?.images || [], evidenceImageAccessMap);
    const witnessEvidenceSections = evidenceWithIds
      .map((item) => item.file)
      .find((file) => String(file?.type || "").trim().toLowerCase() === "witness_statements")?.sections;
    const previousEvidence = activeEvidenceIndex > 0 ? evidenceWithIds[activeEvidenceIndex - 1] : null;
    const nextEvidence =
      activeEvidenceIndex >= 0 && activeEvidenceIndex < evidenceWithIds.length - 1
        ? evidenceWithIds[activeEvidenceIndex + 1]
        : null;

    return (
      <>
        <Helmet>
          <title>{`${String(activeEvidence?.title || viewerCopy.customCaseEvidence)} | ${title}`}</title>
        </Helmet>

        <section className="mx-auto max-w-[1180px] px-1.5 py-2 text-white sm:px-4 sm:py-5 md:p-7">
          <div className="relative overflow-hidden rounded-[22px] border border-amber-200/15 bg-[radial-gradient(circle_at_top_left,rgba(120,53,15,0.28),transparent_28%),linear-gradient(135deg,#08080b,#16110a,#0f172a)] p-2.5 shadow-[0_24px_70px_rgba(0,0,0,0.45)] sm:rounded-[32px] sm:p-6">
            <div className="flex flex-col items-start gap-2.5 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.24em] text-[#d0c296] sm:text-xs sm:tracking-[0.28em]">
                  {viewerCopy.customCaseEvidence}
                </p>
                <h1 className={`mt-1 text-white sm:mt-2 ${CASE_VIEW_DISPLAY_TITLE_CLASS}`}>
                  {String(activeEvidence?.title || activeEvidence?.type || viewerCopy.customCaseEvidence)}
                </h1>
                <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#7ed0e3] sm:mt-2 sm:text-xs sm:tracking-[0.22em]">
                  {getLocalizedCustomCaseEvidenceTypeLabel(String(activeEvidence?.type || "evidence"), viewerLang)}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link
                  to={currentCaseBasePath}
                  className="rounded-full border border-cyan-300/35 bg-cyan-400/10 px-3 py-2 text-xs font-semibold text-cyan-100 hover:bg-cyan-400/15 sm:px-4 sm:text-sm"
                >
                  {viewerCopy.backToCaseFile}
                </Link>
                {canCreateOwnerPdf ? (
                  <button
                    type="button"
                    onClick={() => {
                      devLog("route", "custom_case_access_pdf_modal_opened", {
                        accessTokenPresent: Boolean(accessToken),
                        accessMode: "owner",
                        generatedCaseId: String(caseAccess?.generatedCaseId || ""),
                        evidenceCount: selectedPrintEvidenceIds.length || printableEvidenceIds.length,
                      });
                      setShowPrintModal(true);
                    }}
                    className="rounded-full border border-[#d0c296]/30 bg-[#d0c296]/10 px-3 py-2 text-xs font-semibold text-[#f6f0e0] hover:bg-[#d0c296]/18 sm:px-4 sm:text-sm"
                  >
                    {viewerCopy.createPdf}
                  </button>
                ) : null}
                {canOpenOwnerPacketPdf ? (
                  <a
                    href={caseAccess.packetPdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-2 text-xs font-semibold text-white/90 hover:bg-white/[0.1] sm:px-4 sm:text-sm"
                  >
                    {viewerCopy.openPdfPacket}
                  </a>
                ) : null}
              </div>
            </div>

            <div className="mt-3 grid gap-2 md:mt-6 md:grid-cols-4 md:gap-4">
              <div className="rounded-[16px] border border-white/10 bg-black/20 p-2 sm:rounded-2xl sm:p-4">
                <p className="text-[11px] uppercase tracking-[0.16em] text-white/45 sm:text-xs sm:tracking-wide">{viewerCopy.metadata.date}</p>
                <p className={`mt-1 text-white/85 sm:mt-1.5 ${CASE_VIEW_METRIC_VALUE_CLASS}`}>
                  {String(activeEvidence?.date || viewerCopy.metadata.noDate)}
                </p>
              </div>
              <div className="rounded-[16px] border border-white/10 bg-black/20 p-2 sm:rounded-2xl sm:p-4">
                <p className="text-[11px] uppercase tracking-[0.16em] text-white/45 sm:text-xs sm:tracking-wide">{viewerCopy.metadata.source}</p>
                <p className={`mt-1 text-white/85 sm:mt-1.5 ${CASE_VIEW_METRIC_VALUE_CLASS}`}>
                  {String(activeEvidence?.source || viewerCopy.metadata.noSource)}
                </p>
              </div>
              <div className="rounded-[16px] border border-white/10 bg-black/20 p-2 sm:rounded-2xl sm:p-4">
                <p className="text-[11px] uppercase tracking-[0.16em] text-white/45 sm:text-xs sm:tracking-wide">{viewerCopy.metadata.confidentiality}</p>
                <p className={`mt-1 text-white/85 sm:mt-1.5 ${CASE_VIEW_METRIC_VALUE_CLASS}`}>
                  {String(activeEvidence?.confidentiality || viewerCopy.metadata.internal)}
                </p>
              </div>
              <div className="rounded-[16px] border border-white/10 bg-black/20 p-2 sm:rounded-2xl sm:p-4">
                <p className="text-[11px] uppercase tracking-[0.16em] text-white/45 sm:text-xs sm:tracking-wide">{viewerCopy.metadata.evidenceType}</p>
                <p className={`mt-1 text-white/85 sm:mt-1.5 ${CASE_VIEW_METRIC_VALUE_CLASS}`}>
                  {getLocalizedCustomCaseEvidenceTypeLabel(String(activeEvidence?.type || "evidence"), viewerLang)}
                </p>
              </div>
            </div>

            <div className="mt-3 rounded-[18px] border border-amber-100/12 bg-[linear-gradient(180deg,rgba(255,248,235,0.06),rgba(255,255,255,0.02))] p-3 sm:mt-6 sm:rounded-3xl sm:p-5">
              {renderEvidenceSections(
                {
                  ...activeEvidence,
                  images: selectedEvidenceImageAssets,
                },
                {
                  suspects,
                  suspectImageAccessMap,
                  caseId: String(caseAccess?.generatedCaseId || ""),
                  caseTitle: String(payload?.title || payload?.case_title || caseAccess?.title || ""),
                  evidenceTypeLabel: getLocalizedCustomCaseEvidenceTypeLabel(
                    String(activeEvidence?.type || "evidence"),
                    viewerLang
                  ),
                  copy: {
                    eyebrow: viewerCopy.customCaseEvidence,
                    sections: viewerCopy.sections,
                    images: viewerCopy.imageAssets,
                  },
                  media: viewerCopy.media,
                  panels: viewerCopy.panels,
                  finalView: true,
                  witnessSections: Array.isArray(witnessEvidenceSections) ? witnessEvidenceSections : [],
                  onOpenImage: (url: string) => {
                    if (!url) return;
                    window.open(url, "_blank", "noopener,noreferrer");
                  },
                  solveGate: {
                    enabled: true,
                    mode: "custom",
                    suspects,
                    suspectImageAccessMap,
                    actorLabel: String(payload?.first_name || payload?.firstName || viewerCopy.warrant.detectiveLabel),
                    currentRankLabel: viewerCopy.warrant.detectiveRank,
                    solveStatus: (caseAccess as any)?.solveStatus || null,
                    onSubmitWarrant: handleSubmitCustomWarrant,
                    onDevResetWarrant: handleDevResetCustomWarrant,
                    busy: warrantBusy,
                    feedback: warrantFeedback,
                    error: warrantError,
                    copy: viewerCopy.solution,
                  },
                }
              )}
            </div>

            {renderEvidencePager({
              previousItem: previousEvidence
                ? {
                    title: String(previousEvidence.file?.title || ""),
                    type: String(previousEvidence.file?.type || ""),
                    href: buildCaseHref(`/evidence/${previousEvidence.evidenceId}`),
                  }
                : null,
              nextItem: nextEvidence
                ? {
                    title: String(nextEvidence.file?.title || ""),
                    type: String(nextEvidence.file?.type || ""),
                    href: buildCaseHref(`/evidence/${nextEvidence.evidenceId}`),
                  }
                : null,
              copy: viewerCopy.pager,
              lang: viewerLang,
            })}
            <AgencyPrintOptionsModal
              open={showPrintModal}
              onClose={() => setShowPrintModal(false)}
              printTheme={printTheme}
              onThemeChange={setPrintTheme}
              evidenceFiles={evidenceFiles}
              selectedEvidenceIds={selectedPrintEvidenceIds}
              onEvidenceToggle={togglePrintEvidenceId}
              onToggleAllEvidence={handleToggleAllPrintEvidence}
              onDownloadPdf={handleDownloadPdf}
              pdfBusy={pdfDownloadBusy}
              copy={viewerCopy.printModal}
            />
          </div>
        </section>
      </>
    );
  }

  const caseBackgroundAsset =
    evidenceFiles.find((file: any) => String(file?.type || "").trim().toLowerCase() === "case_background") || evidenceFiles[0];
  const caseBackgroundImageUrl = caseBackgroundAsset
    ? getEvidenceThumbnailUrl(caseBackgroundAsset, evidenceImageAccessMap)
    : String(caseAccess?.heroImageUrl || "").trim();
  const caseDynamicCounts = getCaseDynamicCounts(evidenceFiles, suspects);
  const heroChips = [
    isSharedAccess ? viewerCopy.guestShareAccessChip : viewerCopy.privateCustomAccessChip,
    viewerCopy.suspectDossiers.count(caseDynamicCounts.suspectCount),
    viewerCopy.evidenceRecords(caseDynamicCounts.evidenceCount),
    viewerCopy.imageAssets(caseDynamicCounts.imageCount),
  ];

  return (
    <>
      <Helmet>
        <title>{`${title} | ${viewerCopy.pageTitleSuffix}`}</title>
      </Helmet>

      <section className="mx-auto max-w-[1500px] px-3 py-4 text-white sm:px-4 sm:py-5 md:p-7">
        <div className="border-b border-white/8 pb-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#7e6c44] text-sm tracking-[0.28em] text-[#dccca9]">
                C
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#9f9a8e]">{viewerCopy.brandName}</p>
                <p className="text-3xl font-semibold leading-none text-white">{viewerCopy.customCaseFile}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative mt-10 overflow-hidden rounded-[32px] border border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(187,140,64,0.10),transparent_24%),radial-gradient(circle_at_70%_30%,rgba(67,95,138,0.18),transparent_34%),linear-gradient(180deg,rgba(15,19,28,0.96),rgba(9,12,18,0.98))] p-7 shadow-[0_30px_90px_rgba(5,10,30,0.55)]">
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:24px_24px] opacity-20" />
          <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-start">
            <div>
              <p className="text-[11px] uppercase tracking-[0.32em] text-[#d1c39a]">
                {isSharedAccess ? viewerCopy.sharedCustomDelivery : viewerCopy.privateCustomDelivery}
              </p>
              <h1 className={`mt-5 text-[#f0ede6] ${CASE_VIEW_DISPLAY_TITLE_CLASS}`}>{title}</h1>
              <p className={`mt-5 max-w-4xl whitespace-pre-wrap text-[#c8c4bb] ${CASE_VIEW_HERO_READING_CLASS}`}>
                {summary}
              </p>

              <div className="mt-10 grid gap-4 md:grid-cols-3">
                <article className="rounded-[22px] border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#9f9a8e]">{viewerCopy.accessMode}</p>
                  <p className="mt-3 text-2xl font-semibold text-white">
                    {isSharedAccess ? viewerCopy.guestLink : viewerCopy.ownerLink}
                  </p>
                </article>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                {canCreateOwnerPdf ? (
                  <button
                    type="button"
                    onClick={() => {
                      devLog("route", "custom_case_access_pdf_modal_opened", {
                        accessTokenPresent: Boolean(accessToken),
                        accessMode: "owner",
                        generatedCaseId: String(caseAccess?.generatedCaseId || ""),
                        evidenceCount: selectedPrintEvidenceIds.length || printableEvidenceIds.length,
                      });
                      setShowPrintModal(true);
                    }}
                    className="rounded-full border border-[#d0c296]/30 bg-[#d0c296]/10 px-4 py-2 text-[11px] uppercase tracking-[0.24em] text-[#f6f0e0] transition hover:bg-[#d0c296]/18"
                  >
                    {viewerCopy.createPdf}
                  </button>
                ) : null}
                {heroChips.map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-[11px] uppercase tracking-[0.24em] text-[#d2cdc1]"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-[28px] border border-white/10 bg-[radial-gradient(circle_at_30%_20%,rgba(90,116,153,0.18),transparent_16%),linear-gradient(180deg,rgba(15,28,46,0.95),rgba(9,16,29,0.9))] p-4">
              <div className="relative overflow-hidden rounded-[22px] border border-white/10 bg-black/30">
                {caseBackgroundImageUrl ? (
                  <img
                    src={caseBackgroundImageUrl}
                    alt={`${title} case background`}
                    className="aspect-[16/9] w-full object-cover saturate-[0.9] contrast-[1.05] brightness-[0.86]"
                    loading="lazy"
                  />
                ) : (
                  <div className="aspect-[16/9] bg-[radial-gradient(circle_at_30%_20%,rgba(103,132,170,0.18),transparent_12%),linear-gradient(180deg,rgba(12,20,32,0.98),rgba(10,15,24,0.88))]" />
                )}
                <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(3,8,18,0.08),rgba(3,8,18,0.32))]" />
              </div>
              <div className="mt-3 rounded-2xl border border-white/8 bg-black/20 p-4">
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">{viewerCopy.evidenceVolume}</p>
                <p className="mt-2 text-lg font-semibold text-white">{viewerCopy.sections(caseDynamicCounts.sectionCount)}</p>
              </div>
            </div>
          </div>
        </div>

        {!isSharedAccess ? (
          <div className="mt-7 rounded-[30px] border border-white/10 bg-[linear-gradient(180deg,rgba(15,13,21,0.98),rgba(10,11,16,0.96))] p-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-2xl">
                <p className="text-[11px] uppercase tracking-[0.24em] text-[#d0c296]">{viewerCopy.share.sectionEyebrow}</p>
                <h2 className={CASE_VIEW_SECTION_HEADING_CLASS}>{viewerCopy.share.sectionTitle}</h2>
                <p className={`mt-3 text-white/68 ${CASE_VIEW_SUPPORTING_READING_CLASS}`}>
                  {viewerCopy.share.sectionBody}
                </p>
              </div>
              <div className="flex w-full flex-col gap-3 lg:max-w-sm">
                <div className="w-full rounded-2xl border border-white/10 bg-black/25 px-4 py-3 text-sm text-white/82">
                  {viewerCopy.share.expiresIn24Hours}
                </div>
                <button
                  type="button"
                  onClick={handleCreateShareLink}
                  disabled={shareBusy}
                  className="inline-flex items-center justify-center rounded-full bg-[#d7263d] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#f23c56] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {shareBusy ? viewerCopy.share.createBusy : viewerCopy.share.createIdle}
                </button>
              </div>
            </div>

            {lastCreatedShareUrl ? (
              <div className="mt-5 rounded-[22px] border border-emerald-300/15 bg-emerald-500/10 p-4">
                <p className="text-sm font-semibold text-emerald-100">{viewerCopy.share.newestLink}</p>
                <p className="mt-3 break-all text-sm text-emerald-50/90">{lastCreatedShareUrl}</p>
                <button
                  type="button"
                  onClick={handleCopyCreatedShareLink}
                  className="mt-3 inline-flex rounded-full border border-emerald-200/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-white"
                >
                  {viewerCopy.share.copyLink}
                </button>
              </div>
            ) : null}

            {shareFeedback ? (
              <p className="mt-4 rounded-2xl border border-emerald-300/15 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
                {shareFeedback}
              </p>
            ) : null}
            {shareError ? (
              <p className="mt-4 rounded-2xl border border-rose-300/15 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
                {shareError}
              </p>
            ) : null}

            <div className="mt-5 grid gap-3">
              {shareLinks.length > 0 ? (
                shareLinks.map((shareLink) => (
                  <div
                    key={shareLink.id}
                    className="flex flex-col gap-3 rounded-[22px] border border-white/10 bg-white/[0.03] p-4 lg:flex-row lg:items-center lg:justify-between"
                  >
                    <div>
                      <p className="text-sm font-semibold text-white">{shareLink.label || viewerCopy.share.guestLinkFallback}</p>
                      <p className="mt-1 text-xs uppercase tracking-[0.18em] text-white/45">
                        {shareLink.mode} • {viewerCopy.share.accessCount(shareLink.accessCount)}
                      </p>
                      <p className="mt-2 text-sm text-white/62">
                        {viewerCopy.share.expiresPrefix}{" "}
                        {shareLink.expiresAt
                          ? formatCustomCaseViewerDateTime(shareLink.expiresAt, viewerLang, viewerCopy.share.expiresSoon)
                          : viewerCopy.share.expiresSoon}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRevokeShareLink(shareLink.id)}
                      className="inline-flex items-center justify-center rounded-full border border-rose-300/20 bg-rose-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-rose-100 transition hover:bg-rose-500/16"
                    >
                      {viewerCopy.share.revoke}
                    </button>
                  </div>
                ))
              ) : (
                <div className="rounded-[22px] border border-dashed border-white/14 bg-white/[0.03] p-4 text-sm text-white/55">
                  {viewerCopy.share.noActiveLinks}
                </div>
              )}
            </div>
          </div>
        ) : null}

        <div className="mt-7 grid gap-6 lg:grid-cols-[1.12fr_0.88fr]">
          <div className="space-y-6">
            <article className="rounded-[28px] border border-white/10 bg-[#090e16]/95 p-6">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.28em] text-[#d0c296]">{viewerCopy.video.eyebrow}</p>
                  <h2 className="mt-2 text-3xl font-bold tracking-tight text-white">{viewerCopy.video.title}</h2>
                </div>
                {rawBriefingVideoUrl ? (
                  <a
                    href={rawBriefingVideoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full border border-cyan-300/25 bg-cyan-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100"
                  >
                    {viewerCopy.video.open}
                  </a>
                ) : null}
              </div>

              {briefingVideoUrl ? (
                <div className="overflow-hidden rounded-2xl border border-white/10 bg-black">
                  <iframe
                    src={briefingVideoUrl}
                    title={viewerCopy.video.iframeTitle}
                    className="aspect-video w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-white/20 bg-white/5 p-5 text-sm text-zinc-300">
                  {viewerCopy.video.empty}
                </div>
              )}
            </article>

            <article className="rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.98),rgba(9,13,20,0.96))] p-6">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.28em] text-[#d0c296]">{viewerCopy.briefing.eyebrow}</p>
                  <h3 className={CASE_VIEW_SECTION_HEADING_CLASS}>{viewerCopy.briefing.title}</h3>
                </div>
                {!isSharedAccess ? (
                  <Link
                    to={`/custom/intake/${encodeURIComponent(accessToken)}`}
                    className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80 hover:bg-white/[0.08]"
                  >
                    {viewerCopy.briefing.buildStatusLink}
                  </Link>
                ) : null}
              </div>
              <p className={`mt-4 whitespace-pre-wrap text-[#d0d5df] ${CASE_VIEW_PRIMARY_READING_CLASS}`}>
                {briefingBody || viewerCopy.briefing.defaultBody}
              </p>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                  <p className="text-xs uppercase tracking-[0.22em] text-[#9f9a8e]">{viewerCopy.briefing.setting}</p>
                  <p className="mt-2 text-base font-semibold text-white">{String(payload?.setting?.place || viewerCopy.briefing.unknownPlace)}</p>
                  <p className={`mt-1 text-white/60 ${CASE_VIEW_SUPPORTING_READING_CLASS}`}>
                    {String(payload?.setting?.time || viewerCopy.briefing.unknownTime)}
                  </p>
                </div>
                <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                  <p className="text-xs uppercase tracking-[0.22em] text-[#9f9a8e]">{viewerCopy.briefing.delivery}</p>
                  <p className="mt-2 text-base font-semibold text-white">{viewerCopy.briefing.deliveryValue}</p>
                  <p className={`mt-1 text-white/60 ${CASE_VIEW_SUPPORTING_READING_CLASS}`}>
                    {viewerCopy.briefing.deliveryBody}
                  </p>
                </div>
              </div>
            </article>
          </div>

          <aside className="rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.98),rgba(9,13,20,0.96))] p-5">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.28em] text-[#d0c296]">{viewerCopy.archive.eyebrow}</p>
                <h3 className={CASE_VIEW_SECTION_HEADING_CLASS}>{viewerCopy.archive.title}</h3>
              </div>
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-white/65">{viewerCopy.archive.accessLabel}</span>
            </div>

            <div className="space-y-3">
              {evidenceFiles.map((file: any, index: number) => {
                const evidenceLink = buildCaseHref(
                  `/evidence/${evidenceWithIds[index]?.evidenceId || buildEvidenceId(file, index)}`
                );
                const isVictimProfile = String(file?.type || "").trim().toLowerCase() === "victim_profile";

                return (
                  <div
                    key={`${file?.id || file?.title || "evidence"}-${index}`}
                    className="rounded-[24px] border border-white/8 bg-white/[0.03] p-3"
                  >
                    <Link
                      to={evidenceLink}
                      className="block text-left transition hover:opacity-90"
                      onClick={() =>
                        devLog("route", "custom_case_access_evidence_opened", {
                          accessTokenPresent: Boolean(accessToken),
                          evidenceId: evidenceWithIds[index]?.evidenceId || buildEvidenceId(file, index),
                          evidenceType: String(file?.type || ""),
                        })
                      }
                    >
                      <p className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">
                        {getLocalizedCustomCaseEvidenceTypeLabel(String(file?.type || "evidence"), viewerLang)}
                      </p>
                      <p className={CASE_VIEW_CARD_TITLE_CLASS}>
                        {String(file?.title || `Evidence ${index + 1}`)}
                      </p>
                      <span className="mt-2 inline-block text-[11px] uppercase tracking-[0.18em] text-[#7ed0e3]">
                        {viewerCopy.archive.openFile}
                      </span>
                    </Link>

                    {isVictimProfile && suspects.length > 0 ? (
                      <div className="mt-3 border-t border-white/10 pt-3">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-amber-100/65">{viewerCopy.archive.includedSuspectDossiers}</p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {suspects.map((suspect: any, suspectIndex: number) => (
                            <span
                              key={`custom-suspect-chip-${suspectIndex}`}
                              className="rounded-full border border-amber-200/25 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-100/90"
                            >
                              {String(suspect?.name || suspect?.full_name || viewerCopy.suspectDossiers.fallbackName(suspectIndex))}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </aside>
        </div>

        <div className="mt-7 rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.98),rgba(9,13,20,0.96))] p-5 sm:p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#d0c296]">{viewerCopy.suspectDossiers.eyebrow}</p>
              <h3 className={CASE_VIEW_SECTION_HEADING_CLASS}>{viewerCopy.suspectDossiers.title}</h3>
            </div>
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-white/70">
              {viewerCopy.suspectDossiers.count(suspects.length)}
            </span>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {suspects.map((suspect: any, index: number) => {
              const storageKey = String(suspect?.image_storage_key || suspect?.imageStorageKey || "").trim();
              const imageUrl = storageKey ? String(suspectImageAccessMap[storageKey] || "").trim() : "";
              return (
                <article
                  key={`${String(suspect?.name || "suspect")}-${index}`}
                  className="overflow-hidden rounded-[24px] border border-white/10 bg-black/20"
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={String(suspect?.name || viewerCopy.suspectDossiers.fallbackName(index))}
                      className="aspect-[4/5] w-full object-cover"
                    />
                  ) : (
                    <div className="flex aspect-[4/5] items-center justify-center bg-white/[0.03]">
                      <UserRound className="h-10 w-10 text-white/28" />
                    </div>
                  )}
                  <div className="p-4">
                    <p className="text-lg font-semibold text-white">
                      {String(suspect?.name || viewerCopy.suspectDossiers.fallbackName(index))}
                    </p>
                    {suspect?.occupation ? (
                      <p className={`mt-1 text-[#eed7a3] ${CASE_VIEW_SUPPORTING_READING_CLASS}`}>
                        {String(suspect.occupation)}
                      </p>
                    ) : null}
                    {suspect?.relation ? (
                      <p className={`mt-2 text-white/66 ${CASE_VIEW_SUPPORTING_READING_CLASS}`}>
                        {String(suspect.relation)}
                      </p>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
        <AgencyPrintOptionsModal
          open={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          printTheme={printTheme}
          onThemeChange={setPrintTheme}
          evidenceFiles={evidenceFiles}
          selectedEvidenceIds={selectedPrintEvidenceIds}
          onEvidenceToggle={togglePrintEvidenceId}
          onToggleAllEvidence={handleToggleAllPrintEvidence}
          onDownloadPdf={handleDownloadPdf}
          pdfBusy={pdfDownloadBusy}
          copy={viewerCopy.printModal}
        />
      </section>
    </>
  );
}
